import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "../../../services/apiClient";
import { requestPasswordReset } from "../api/auth";
import { FormFieldLabel } from "../../../components/FormFieldLabel";
import { AuthFormBase } from "../components/AuthFormBase";

const primaryButtonClasses = "pm-btn pm-btn-primary w-full";
const inputClasses =
  "block w-full rounded-xl border-0 py-2.5 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 disabled:bg-slate-50 disabled:text-slate-500 disabled:ring-slate-200 dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-700 dark:placeholder-slate-500 dark:disabled:bg-slate-800";
const fieldClasses = "flex flex-col gap-2";

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setMessage(null);

    if (!email.trim()) {
      setError(t("auth.forgot_password.email_required"));
      return;
    }

    setIsSubmitting(true);
    try {
      const responseMessage = await requestPasswordReset(email.trim());
      setMessage(
        responseMessage ??
          t("auth.forgot_password.success_message")
      );
      setEmail("");
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.body?.message ?? t("auth.forgot_password.send_error"));
      } else {
        setError(t("common.error.unexpected"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthFormBase
      onSubmit={handleSubmit}
      error={error}
      success={message}
      showSocialAuth={false}
      submitButton={
        <button type="submit" className={primaryButtonClasses} disabled={isSubmitting}>
          {isSubmitting ? t("auth.forgot_password.sending") : t("auth.forgot_password.button")}
        </button>
      }
    >
      <div className={fieldClasses}>
        <label htmlFor="forgot-email">
          <FormFieldLabel label={t("forms.email.label")} />
        </label>
        <input
          id="forgot-email"
          type="email"
          inputMode="email"
          className={inputClasses}
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
        <small className="mt-1 text-sm text-slate-500 dark:text-slate-400">{t("auth.forgot_password.helper_text")}</small>
      </div>
    </AuthFormBase>
  );
}
